// src/utils/retry.ts — FINAL
import type { RetryPolicy } from "../types";

export async function withRetry<T>(
    fn: (attempt: number, signal: AbortSignal) => Promise<T>,
    policy: RetryPolicy,
    onAttempt?: (attempt: number, err?: unknown) => void,
): Promise<T> {
    const {
        retries,
        baseDelayMs,
        maxDelayMs,
        jitter = true,
        timeoutMs = 12000,
    } = policy;
    for (let attempt = 0; attempt <= retries; attempt++) {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort("Timeout"), timeoutMs);
        try {
            onAttempt?.(attempt);
            const result = await fn(attempt, ctrl.signal);
            clearTimeout(t);
            return result;
        } catch (err) {
            clearTimeout(t);
            onAttempt?.(attempt, err);
            if (attempt === retries) throw err;
            const backoff = Math.min(
                baseDelayMs * Math.pow(2, attempt),
                maxDelayMs,
            );
            const sleep = jitter ? jitterMs(backoff) : backoff;
            await delay(sleep);
        }
    }
    throw new Error("withRetry exhausted");
}

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
const jitterMs = (ms: number) => {
    const variance = ms * 0.5;
    return Math.max(0, ms - variance + Math.random() * (2 * variance));
};
